import { useCallback, useState } from "react";
import { Button, Text } from "@radix-ui/themes";
import { ChatBubbleIcon } from "@radix-ui/react-icons";
import { Dialog } from "@/components/shared/dialog";
import { QuackChatConfigResponse } from "@/components/quack-chat/QuackChatConfig.model";
import { QuackChatSystemMessageType } from "@/v2/entities/quackchat/Quackchat.model";
import { QuackChatSystemMessageOverride } from "./QuackChatSystemMessageOverride";
import { toast } from "sonner";
import ExitEditDialog from "./ExitEditDialog";

interface QuackchatSystemMessageConfigurationProps {
  config: QuackChatConfigResponse;
  defaultMessageType?: QuackChatSystemMessageType;
  simulatorId: string | undefined;
}

export function QuackchatSystemMessageConfiguration({
  config,
  defaultMessageType,
  simulatorId,
}: QuackchatSystemMessageConfigurationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleStopEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleError = useCallback(() => {
    setIsOpen(false);
    toast.error("Unable to load system message");
  }, []);

  const tryingToClose = useCallback(() => {
    if (isEditing) {
      setShowAlert(true);
      return;
    }

    setIsOpen(false);
  }, [isEditing]);

  const handleCancelExitEdit = useCallback(() => {
    setShowAlert(false);
  }, []);

  const handleContinueExitEdit = useCallback(() => {
    setShowAlert(false);
    setIsEditing(false);
    setIsOpen(false);
  }, []);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger>
          <Button
            size={"1"}
            color={"gray"}
            variant={"outline"}
            onClick={() => setIsOpen(true)}
          >
            <ChatBubbleIcon />
            Customize Messages
          </Button>
        </Dialog.Trigger>

        {isOpen && (
          <Dialog.Content
            size={"large"}
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              tryingToClose();
            }}
            onInteractOutside={(event) => {
              event.preventDefault();
              tryingToClose();
            }}
            onPointerDownOutside={(event) => {
              event.preventDefault();
              tryingToClose();
            }}
          >
            <Dialog.Title>
              <Text size={"2"} color={"gray"}>
                Customize Messages
              </Text>

              <Dialog.Close
                onClick={(event) => {
                  event.preventDefault();
                  tryingToClose();
                }}
              />
            </Dialog.Title>

            <QuackChatSystemMessageOverride
              config={config}
              defaultMessageType={defaultMessageType}
              isEditing={isEditing}
              simulatorId={simulatorId}
              onStartEdit={handleStartEdit}
              onStopEdit={handleStopEdit}
              onError={handleError}
            />
          </Dialog.Content>
        )}
      </Dialog>

      <ExitEditDialog
        open={showAlert}
        onCancel={handleCancelExitEdit}
        onContinue={handleContinueExitEdit}
      />
    </>
  );
}
